import React, { useEffect, useMemo, useState } from 'react';
import { Input, TitleWithActions } from 'components/components';
import StartupWallet from 'components/components/StartupWallet/View';
import StaticNotification from 'components/components/Notification/StaticNotification';
import { ManagedStartupType, ManagedStartupWalletType } from 'packages/venture_builder_repository/types';
import { startupLabel, toStartupWallet } from 'packages/venture_builder_repository/utils/converters';
import './VentureBuilderWallet.scss';

const VentureBuilderWallet = ({ startups }: { startups: ManagedStartupType[] }) => {
  const [startupId, setStartupId] = useState<number | 'all'>('all');

  useEffect(() => {
    if (startupId !== 'all' && !startups.some(({ id }) => id === startupId)) {
      setStartupId('all');
    }
  }, [startups, startupId]);

  const wallet = useMemo(() => {
    const selected = startupId === 'all' ? startups : startups.filter(({ id }) => id === startupId);
    if (!selected.length || selected.some((startup) => !startup.wallet)) return null;
    // A display-only summary: it never becomes an order's funding source.
    return selected.reduce<ManagedStartupWalletType>((sum, startup) => ({
      balance: sum.balance + startup.wallet!.balance,
      total: sum.total + startup.wallet!.total,
      consumed: sum.consumed + startup.wallet!.consumed,
      blocked: sum.blocked + startup.wallet!.blocked,
    }), {
      balance: 0, total: 0, consumed: 0, blocked: 0,
    });
  }, [startups, startupId]);

  return (
    <section className="startup-wallet" aria-label="Wallet statistics">
      <div className="startup-wallet__header">
        <TitleWithActions title="Wallet" />
        <Input label="Startup" layout="vertical" className="venture-builder-wallet__filter">
          <Input.Select
            showSearch
            value={startupId}
            onChange={setStartupId}
            options={[
              { value: 'all', label: 'All startups' },
              ...startups.map((startup) => ({ value: startup.id, label: startupLabel(startup) })),
            ]}
          />
        </Input>
      </div>
      {wallet ? (
        <>
          <StartupWallet wallet={toStartupWallet(wallet)} userType="Venture Builder" includeGraph showHeader={false} />
          {startupId === 'all' && (
            <StaticNotification
              type="info"
              message="Combined wallet statistics. Each startup can only spend its own balance."
            />
          )}
        </>
      ) : (
        <StaticNotification
          type="warning"
          message={startupId === 'all'
            ? 'Wallet information is unavailable for one or more startups. Select a startup to view its wallet.'
            : 'Wallet information is unavailable for this startup.'}
        />
      )}
    </section>
  );
};

export default VentureBuilderWallet;
